import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ITestRepository from '../../2_domain/test/iTestRepository';
import { TestStatusDto, TestSummary } from '../../2_domain/test/test';
import Get from '../../lib/di/get';
import promiseResolver from '../../lib/util/promiseResolver';
import { parseParamsToInt } from '../../lib/util/stringUtils';

const emptyPageResult: Page = {
  totalElements: 0,
  pageNumber: 20,
};

const useTestListPageController = (testStatuses: TestStatusDto[]) => {
  const testRepo = Get.find<ITestRepository>('ITestRepository');
  const [searchParams, setSearchParams] = useSearchParams();
  const params = {
    from: searchParams.get('from'),
    end: searchParams.get('end'),
    pageSize: parseParamsToInt(searchParams.get('pageSize')),
    pageNumber: parseParamsToInt(searchParams.get('pageNumber')),
    status: searchParams.get('status'),
    keyword: searchParams.get('keyword'),
  };

  const navigate = useNavigate();

  const [pagination, setPagination] = useState<Pagination>({
    isAscending: false,
    pageNumber: params.pageNumber ?? 0,
    pageSize: params.pageSize ?? 20,
  });

  const status = testStatuses.filter(
    (val) => encodeURIComponent(val.status) === params.status
  )[0];

  const [pageResult, setPageResult] = useState(emptyPageResult);
  const [searchText, setSearchText] = useState(params.keyword ?? undefined);
  const [selectedStatus, setSelectedStatus] = useState<TestStatus | 'all'>(
    encodeURIComponent(status?.status) === 'undefined' ? 'all' : status.status
  );
  const [isLoading, setIsLoading] = useState(true);
  const [fromDate, setFromDate] = useState<Date | undefined>(
    params.from ? new Date(params.from) : undefined
  );
  const [endDate, setEndDate] = useState<Date | undefined>(
    params.end ? new Date(params.end) : undefined
  );
  const [tests, setTests] = useState<TestSummary[]>([]);

  useEffect(() => {
    const status = testStatuses.filter(
      (val) => encodeURIComponent(val.status) === params.status
    )[0];
    setSelectedStatus(
      encodeURIComponent(status?.status) === 'undefined' ? 'all' : status.status
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const onSelect = (status: string) => {
    if (status === 'all') setSearchParams({});
    else setSearchParams({ status });
  };

  const loadTests = () =>
    promiseResolver({
      promise: testRepo.getTestSummaries({
        status: selectedStatus,
        pagination: pagination,
        searchText: searchText,
        fromDate: fromDate,
        endDate: endDate,
      }),
      isLoadingCallback: setIsLoading,
      successCallback: (data) => {
        setPageResult(data.page);
        setTests(data.list);
      },
    });
  const onSearch = async () => {
    loadTests();
  };

  useEffect(() => {
    loadTests();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedStatus]);

  const onRefresh = () => {
    setSearchText('');
    setSelectedStatus('all');
  };

  const setPageNumber = (newPageNumber: number) => {
    setPagination((prev) => {
      return { ...prev, pageNumber: newPageNumber };
    });
  };

  const setPageSize = (newPageSize: number) => {
    setPagination((prev) => {
      return { ...prev, pageSize: newPageSize };
    });
  };

  const setSortFilter = (sortFiler: SortFilter) => {
    setPagination((prev) => {
      return { ...prev, isAscending: sortFiler === 'Oldest' };
    });
  };

  const onNewTestHandler = async () => {
    navigate('../order');
  };

  const onTestClickHandler = (test: TestSummary) => {
    navigate(`../${test.id}`);
  };

  useEffect(() => {
    onSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination]);

  return {
    onSelect,
    pagination,
    pageResult,
    searchText,
    setSearchText,
    selectedStatus,
    setSelectedStatus,
    onSearch,
    onRefresh,
    beginDate: fromDate,
    setBeginDate: setFromDate,
    endDate,
    setEndDate,
    setSortFilter,
    setPageNumber,
    setPageSize,
    onNewTestHandler,
    tests,
    isLoading,
    onTestClickHandler,
  };
};

export default useTestListPageController;
